import React from "react";
import { graphql } from "gatsby";
import { RevealOnMount } from "src/components/reveal-on-mount";
import { SEO } from "src/components/SEO";
import { MetaInfo } from "src/types/meta";
import { ProjectGallery } from "src/components/project/gallery";
import { ProjectHeader } from "src/components/project/header";
import { ProjectFooter } from "src/components/project/footer";
import { blocksToPlainText } from "src/utils/blocksToPlainText";

export const query = graphql`
	fragment relatedProjectContent on SanityProject {
		content {
			main {
				title
				slug {
					current
				}
				indexThumbnail {
					asset {
						_id
					}
				}
			}
		}
	}

	query ProjectQuery(
		$id: String
		$nextProjectId: String
		$previousProjectId: String
		$relatedProjectIds: [String]
	) {
		project: sanityProject(id: { eq: $id }) {
			content: _rawContent(resolveReferences: { maxDepth: 6 })
		}
		nextProject: sanityProject(id: { eq: $nextProjectId }) {
			...relatedProjectContent
		}
		previousProject: sanityProject(id: { eq: $previousProjectId }) {
			...relatedProjectContent
		}
		relatedProjects: allSanityProject(
			sort: { fields: order, order: ASC }
			filter: { id: { in: $relatedProjectIds } }
		) {
			edges {
				node {
					...relatedProjectContent
				}
			}
		}
	}
`;

interface GalleryItem {
	_key: string;
	type: string;
	copy?: any;
	image?: any;
}

interface RelatedProject {
	_id: string;
	content: {
		main: {
			title: string;
			slug: {
				current: string;
			};
			indexThumbnail: {
				asset: {
					_id: string;
				};
			};
		};
	};
}

interface ProjectProps {
	data: {
		project: {
			content: {
				main: {
					slug: {
						current: string;
					};
					title: string;
					gallery: [{ _key: string; items: GalleryItem[] }];
					tags: {
						tags: any[];
					};
					indexThumbnail: any;
					detailsCopy?: any[];
				};
				meta: {
					metaTitle?: string;
					metaDescription?: string;
					openImage?: any;
					twitterImage?: any;
				};
			};
		};
		nextProject: RelatedProject;
		previousProject: RelatedProject;
		relatedProjects: { edges: [{ node: RelatedProject }] };
	};
	location?: any;
}

const ProjectTemplate = ({ location, data }: ProjectProps) => {
	const { main, meta } = data.project.content;
	const { nextProject, previousProject, relatedProjects } = data;
	const primaryTag = main.tags?.tags?.map((tag) => tag.title)[0];

	// set fallback meta tags from content
	const metaInfo: MetaInfo = {
		metaTitle: meta?.metaTitle || main.title,
		metaDescription:
			meta?.metaDescription || blocksToPlainText(main.detailsCopy),
		openImage: meta?.openImage || main.indexThumbnail,
		twitterImage: meta?.twitterImage || meta?.openImage || main.indexThumbnail,
	};

	return (
		<RevealOnMount location={location}>
			<SEO
				metaInfo={metaInfo}
				pagePath={location?.pathname}
				slug={main.slug.current}
			/>
			<ProjectHeader location={location} data={main} />
			<ProjectGallery data={main} />
			{(nextProject || previousProject || relatedProjects) && (
				<ProjectFooter
					data={{ nextProject, previousProject, relatedProjects, primaryTag }}
				/>
			)}
		</RevealOnMount>
	);
};

export default ProjectTemplate;
